import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Image as ImageIcon, Search, Loader2, ChevronLeft, ChevronRight, Check,
} from 'lucide-react';
import { adminAPI } from '@/services/api';

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const FOLDERS = [
  { key: null, label: 'All' },
  { key: 'speakers', label: 'Speakers' },
  { key: 'webinars', label: 'Webinars' },
  { key: 'industries', label: 'Industries' },
  { key: 'seminars', label: 'Seminars' },
  { key: 'gallery', label: 'Gallery' },
];

export default function MediaPickerModal({ open, onClose, onSelect, folder: defaultFolder }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [activeFolder, setActiveFolder] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const perPage = 30;

  const fetchAssets = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const params = { page, per_page: perPage };
      if (activeFolder) params.folder = activeFolder;
      if (search) params.search = search;
      const response = await adminAPI.get('/media/assets', { params });
      setAssets(response.data.assets || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch media:', error);
    }
    setLoading(false);
  }, [open, page, search, activeFolder]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  useEffect(() => {
    if (open) {
      setSelected(null);
      setSearch('');
      setPage(1);
      setActiveFolder(null);
    }
  }, [open]);

  const handleConfirm = () => {
    if (selected) {
      onSelect(selected.public_url, selected);
      onClose();
    }
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Select Image from Media Library</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 min-h-0 flex-1">
          {/* Folder tabs + Search */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex flex-wrap gap-1">
              {FOLDERS.map(({ key, label }) => (
                <Button
                  key={label}
                  variant={activeFolder === key ? 'default' : 'outline'}
                  size="sm"
                  className={`text-xs h-8 ${activeFolder === key ? 'bg-navy-600' : ''}`}
                  onClick={() => { setActiveFolder(key); setPage(1); }}
                >
                  {label}
                </Button>
              ))}
            </div>
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                placeholder="Search..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-8 h-8 text-sm"
                data-testid="media-picker-search"
              />
            </div>
          </div>

          {/* Image Grid */}
          <div className="overflow-y-auto flex-1 min-h-[300px] max-h-[400px] border rounded-lg p-2">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-navy-500" />
              </div>
            ) : assets.length === 0 ? (
              <div className="text-center py-16">
                <ImageIcon className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-400">No images found</p>
              </div>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
                {assets.map((asset) => {
                  const displayName = asset.filename
                    ? asset.filename.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ')
                    : '';
                  return (
                    <button
                      key={asset.id}
                      type="button"
                      onClick={() => setSelected(asset)}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                        selected?.id === asset.id
                          ? 'border-blue-500 ring-2 ring-blue-200'
                          : 'border-transparent hover:border-slate-300'
                      }`}
                      data-testid={`media-pick-${asset.id}`}
                    >
                      <div className="aspect-square overflow-hidden bg-slate-100">
                        {asset.public_url && ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(asset.file_type) ? (
                          <img
                            src={asset.public_url}
                            alt={displayName}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-6 h-6 text-slate-300" />
                          </div>
                        )}
                        {selected?.id === asset.id && (
                          <div className="absolute top-1 right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-600 text-center px-1 py-1 truncate leading-tight" title={displayName}>
                        {displayName}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected info + Pagination */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-3">
              {selected && (
                <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg">
                  <Check className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-blue-700 font-medium truncate max-w-[200px]">{selected.filename}</span>
                  <Badge variant="outline" className="text-[10px]">{formatSize(selected.size)}</Badge>
                </div>
              )}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <span className="text-slate-400 text-xs">{page}/{totalPages}</span>
                <Button variant="outline" size="sm" className="h-7 w-7 p-0"
                  onClick={() => setPage(p => p - 1)} disabled={page === 1}>
                  <ChevronLeft className="w-3.5 h-3.5" />
                </Button>
                <Button variant="outline" size="sm" className="h-7 w-7 p-0"
                  onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>
                  <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="pt-2 border-t">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={!selected}
            className="bg-navy-600 hover:bg-navy-700" data-testid="media-picker-confirm">
            <ImageIcon className="w-4 h-4 mr-2" />
            Select Image
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
